import { Helmet } from "react-helmet-async";
import militaryEagleFlag from "@/assets/military-eagle-flag.jpg";
import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Shield, 
  GraduationCap, 
  Heart, 
  Plane, 
  Phone, 
  CheckCircle,
  Star,
  Users,
  Clock,
  Car,
  MapPin,
  Award,
  Quote,
  ArrowRight,
  Calendar,
  Timer
} from "lucide-react";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";

// BMT Graduation dates for 2025-2026 (Fridays)
const bmtGraduationDates = [
  { date: new Date('2025-12-05'), week: 'Week 51' },
  { date: new Date('2025-12-12'), week: 'Week 52' },
  { date: new Date('2025-12-19'), week: 'Week 53' },
  { date: new Date('2026-01-03'), week: 'Week 1' },
  { date: new Date('2026-01-10'), week: 'Week 2' },
  { date: new Date('2026-01-17'), week: 'Week 3' },
  { date: new Date('2026-01-24'), week: 'Week 4' },
  { date: new Date('2026-01-31'), week: 'Week 5' },
  { date: new Date('2026-02-06'), week: 'Week 6' },
  { date: new Date('2026-02-13'), week: 'Week 7' },
  { date: new Date('2026-02-20'), week: 'Week 8' },
  { date: new Date('2026-02-27'), week: 'Week 9' },
];

const installations = [
  {
    name: "Lackland AFB",
    subtitle: "BMT Graduation Specialist",
    description: "Premium transportation for Air Force Basic Military Training graduations. We take families directly to ceremony locations.",
    icon: GraduationCap,
    link: "/bmt-graduation",
    features: ["Graduation ceremonies", "Coin ceremony", "Family celebrations"],
    highlight: "BMT Graduation"
  },
  {
    name: "Fort Sam Houston",
    subtitle: "BAMC Medical Transport",
    description: "Reliable transportation for medical appointments at Brooke Army Medical Center. Hourly wait service available.",
    icon: Heart,
    link: "/bamc-transportation",
    features: ["Medical appointments", "Wait service", "Recurring schedules"],
    highlight: "Medical Care"
  },
  {
    name: "Randolph AFB",
    subtitle: "Executive Transportation",
    description: "Professional transportation for the 'Showplace of the Air Force.' VIP guests, official business, and family visits.",
    icon: Plane,
    link: "/randolph-afb-transportation",
    features: ["Executive travel", "VIP guests", "Official business"],
    highlight: "Executive Service"
  },
  {
    name: "Camp Bullis",
    subtitle: "Training Installation",
    description: "Reliable transportation to San Antonio's military training installation for personnel and contractors.",
    icon: Shield,
    link: "/camp-bullis-transportation",
    features: ["Training transport", "Early departures", "Contractor access"],
    highlight: "Training Support"
  }
];

const testimonials = [
  {
    name: "The Martinez Family",
    location: "Flew in from California",
    quote: "Our son's BMT graduation was one of the proudest moments of our lives. Rick made it stress-free—picking us up from SAT and taking us right onto Lackland. We didn't miss a single moment.",
    rating: 5
  },
  {
    name: "SSgt. Jennifer W.",
    location: "Active Duty, Randolph AFB",
    quote: "When my parents visit from out of state, Rick is my go-to. They feel safe, comfortable, and well taken care of. It's one less thing I have to worry about during busy work weeks.",
    rating: 5
  },
  {
    name: "David & Carol P.",
    location: "BAMC Patient Family",
    quote: "My wife's appointments at BAMC can run long. Rick waits patiently and is always there when we're ready. His kindness during a difficult time means the world to us.",
    rating: 5
  }
];

const MilitaryFamilies = () => {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [nextGraduation, setNextGraduation] = useState<typeof bmtGraduationDates[0] | null>(null);

  useEffect(() => {
    const calculateCountdown = () => {
      const now = new Date();
      const upcoming = bmtGraduationDates.find(g => g.date > now);
      
      if (upcoming) {
        setNextGraduation(upcoming);
        const diff = upcoming.date.getTime() - now.getTime();
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setCountdown({ days, hours, minutes, seconds });
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Military Family Transportation San Antonio",
    "description": "Premium transportation services for military families at Joint Base San Antonio. Verified base access to Lackland AFB, Fort Sam Houston, and Randolph AFB. BMT Graduation, BAMC medical appointments, and executive military travel.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    },
    "areaServed": [
      { "@type": "MilitaryBase", "name": "Joint Base San Antonio" },
      { "@type": "MilitaryBase", "name": "Lackland Air Force Base" },
      { "@type": "MilitaryBase", "name": "Fort Sam Houston" },
      { "@type": "MilitaryBase", "name": "Randolph Air Force Base" }
    ],
    "serviceType": "Military Family Transportation"
  };

  return (
    <>
      <Helmet>
        <title>Military Family Transportation San Antonio | JBSA Base Access | Rick's Black Car</title>
        <meta 
          name="description" 
          content="Premium transportation for military families at JBSA. Verified base access to Lackland AFB, Fort Sam Houston & Randolph AFB. BMT Graduation, BAMC appointments, family visits. Call (210) 296-7409." 
        />
        <meta name="keywords" content="military family transportation San Antonio, JBSA car service, Lackland AFB families, Fort Sam Houston transportation, Randolph AFB, BMT graduation families" />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/military-families" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <Navbar />
      
      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${militaryEagleFlag})`,
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
          <div className="relative z-10 container mx-auto px-4 text-center">
            <Breadcrumb 
              items={[
                { name: "Services", url: "/services" },
                { name: "Military Families", url: "/military-families" }
              ]} 
            />
            <div className="inline-flex items-center gap-2 bg-primary/30 border-2 border-primary/70 rounded-full px-4 py-2 mb-6 shadow-[0_0_20px_rgba(212,175,55,0.7),0_0_40px_rgba(212,175,55,0.5),0_0_60px_rgba(212,175,55,0.3),0_0_80px_rgba(212,175,55,0.2)] animate-pulse">
              <Shield className="w-5 h-5 text-primary drop-shadow-[0_0_12px_rgba(212,175,55,0.9)]" />
              <span className="text-primary font-bold drop-shadow-[0_0_10px_rgba(212,175,55,0.8)]">Verified JBSA Base Access</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Serving Those Who Serve
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-4 max-w-3xl mx-auto">
              Premium Transportation for Military Families at Joint Base San Antonio
            </p>
            <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
              Lackland AFB • Fort Sam Houston • Randolph AFB
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <Link to="/request-quote">Book Transportation</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg bg-background/10 border-white text-white hover:bg-white hover:text-foreground">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-10 bg-card border-y border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Shield className="h-10 w-10 text-primary mb-3" />
                <span className="font-semibold">Verified Base Access</span>
                <span className="text-sm text-muted-foreground">All JBSA Installations</span>
              </div>
              <div className="flex flex-col items-center">
                <CheckCircle className="h-10 w-10 text-primary mb-3" />
                <span className="font-semibold">DBIDS Credentialed</span>
                <span className="text-sm text-muted-foreground">Background-Checked</span>
              </div>
              <div className="flex flex-col items-center">
                <Car className="h-10 w-10 text-primary mb-3" />
                <span className="font-semibold">Luxury Audi Fleet</span>
                <span className="text-sm text-muted-foreground">Up to 6 Passengers</span>
              </div>
              <div className="flex flex-col items-center">
                <Star className="h-10 w-10 text-primary mb-3" />
                <span className="font-semibold">5-Star Service</span>
                <span className="text-sm text-muted-foreground">Exceptional Reviews</span>
              </div>
            </div>
          </div>
        </section>

        {/* Why Military Families Choose Us */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Military Families Choose Us</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We understand the unique needs of military families. From proud graduation moments to important medical appointments, we're here for you.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <Card className="border-primary/20 text-center">
                <CardContent className="p-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Direct Base Access</h3>
                  <p className="text-muted-foreground">
                    Unlike Uber or Lyft, we have verified credentials to enter JBSA bases without a sponsor. No waiting at the gate—we take you right to your destination.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20 text-center">
                <CardContent className="p-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Family-Focused</h3>
                  <p className="text-muted-foreground">
                    We know these moments matter. Whether it's watching your Airman graduate or supporting a loved one through treatment, we help you focus on what's important.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20 text-center">
                <CardContent className="p-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Flexible Service</h3>
                  <p className="text-muted-foreground">
                    Point-to-point transfers or hourly service with wait time—we adapt to your schedule, not the other way around. 24/7 availability for any need.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* JBSA Installations */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">JBSA Installations We Serve</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Comprehensive transportation services to all Joint Base San Antonio locations
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {installations.map((installation, index) => {
                const Icon = installation.icon;
                return (
                  <Card key={index} className="border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg overflow-hidden">
                    <div className="bg-primary/10 p-6 text-center">
                      <Icon className="h-12 w-12 text-primary mx-auto mb-3" />
                      <h3 className="text-2xl font-bold">{installation.name}</h3>
                      <span className="text-sm text-primary font-medium">{installation.subtitle}</span>
                    </div>
                    <CardContent className="p-6">
                      <p className="text-muted-foreground mb-4">
                        {installation.description}
                      </p>
                      <ul className="space-y-2 mb-6">
                        {installation.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button asChild className="w-full">
                        <Link to={installation.link}>
                          Learn More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center mt-10">
              <Button asChild variant="outline" size="lg">
                <Link to="/services/military-transportation">
                  View All Military Transportation Services
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* BMT Graduation Calendar Widget */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <div className="flex justify-center mb-4">
                <div className="bg-primary/10 p-3 rounded-full">
                  <GraduationCap className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">BMT Graduation Calendar</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Plan ahead for your Airman's graduation. Book transportation early—these weekends fill up fast!
              </p>
            </div>

            {/* Countdown Timer */}
            {nextGraduation && (
              <div className="max-w-3xl mx-auto mb-10">
                <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10">
                  <CardContent className="p-6">
                    <div className="text-center mb-4">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Timer className="h-5 w-5 text-primary" />
                        <span className="text-sm font-medium text-primary">Next Graduation Countdown</span>
                      </div>
                      <p className="text-muted-foreground">
                        {nextGraduation.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="flex justify-center gap-3 md:gap-6">
                      <div className="text-center">
                        <div className="bg-background border border-border rounded-lg p-3 md:p-4 min-w-[60px] md:min-w-[80px] shadow-sm">
                          <span className="text-2xl md:text-4xl font-bold text-primary">{countdown.days}</span>
                        </div>
                        <span className="text-xs text-muted-foreground mt-1 block">Days</span>
                      </div>
                      <div className="text-center">
                        <div className="bg-background border border-border rounded-lg p-3 md:p-4 min-w-[60px] md:min-w-[80px] shadow-sm">
                          <span className="text-2xl md:text-4xl font-bold text-primary">{countdown.hours}</span>
                        </div>
                        <span className="text-xs text-muted-foreground mt-1 block">Hours</span>
                      </div>
                      <div className="text-center">
                        <div className="bg-background border border-border rounded-lg p-3 md:p-4 min-w-[60px] md:min-w-[80px] shadow-sm">
                          <span className="text-2xl md:text-4xl font-bold text-primary">{countdown.minutes}</span>
                        </div>
                        <span className="text-xs text-muted-foreground mt-1 block">Min</span>
                      </div>
                      <div className="text-center">
                        <div className="bg-background border border-border rounded-lg p-3 md:p-4 min-w-[60px] md:min-w-[80px] shadow-sm">
                          <span className="text-2xl md:text-4xl font-bold text-primary">{countdown.seconds}</span>
                        </div>
                        <span className="text-xs text-muted-foreground mt-1 block">Sec</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Graduation Dates Grid */}
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {bmtGraduationDates
                  .filter(g => g.date > new Date())
                  .map((graduation, index) => {
                    const daysUntil = Math.ceil((graduation.date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                    const isNext = index === 0;
                    return (
                      <Card
                        key={index}
                        className={`transition-all hover:shadow-md ${
                          isNext ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <CardContent className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1 mb-2">
                            <Calendar className={`h-4 w-4 ${isNext ? 'text-primary' : 'text-muted-foreground'}`} />
                            {isNext && <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Next</span>}
                          </div>
                          <p className="font-semibold">
                            {graduation.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                          <p className="text-xs text-muted-foreground">{graduation.date.getFullYear()}</p>
                          <p className="text-xs text-muted-foreground mt-1">{graduation.week}</p>
                          <p className={`text-sm font-medium mt-2 ${isNext ? 'text-primary' : 'text-muted-foreground'}`}>
                            {daysUntil} days
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
              <p className="text-center text-xs text-muted-foreground mt-4">
                * Dates subject to change. Verify with official Lackland AFB announcements.
              </p>
            </div>

            <div className="text-center mt-8">
              <Button asChild size="lg">
                <Link to="/bmt-graduation">
                  View Full BMT Graduation Guide
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Pricing Overview */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Transparent Pricing</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Pricing based on distance & time with 20% gratuity included. No hidden fees or surprises.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="border-primary/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-primary/10">
                      <tr>
                        <th className="text-left p-4 font-semibold">Route</th>
                        <th className="text-center p-4 font-semibold">Distance</th>
                        <th className="text-center p-4 font-semibold">Quote</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr>
                        <td className="p-4 font-medium">SAT → Lackland AFB</td>
                        <td className="p-4 text-center text-muted-foreground">~18 mi</td>
                        <td className="p-4 text-center">
                          <Link to="/quick-quote" className="text-primary font-semibold hover:underline">Get Quote</Link>
                        </td>
                      </tr>
                      <tr className="bg-muted/30">
                        <td className="p-4 font-medium">SAT → Fort Sam Houston / BAMC</td>
                        <td className="p-4 text-center text-muted-foreground">~7 mi</td>
                        <td className="p-4 text-center">
                          <Link to="/quick-quote" className="text-primary font-semibold hover:underline">Get Quote</Link>
                        </td>
                      </tr>
                      <tr>
                        <td className="p-4 font-medium">SAT → Randolph AFB</td>
                        <td className="p-4 text-center text-muted-foreground">~20 mi</td>
                        <td className="p-4 text-center">
                          <Link to="/quick-quote" className="text-primary font-semibold hover:underline">Get Quote</Link>
                        </td>
                      </tr>
                      <tr className="bg-muted/30">
                        <td className="p-4 font-medium">Hourly Service</td>
                        <td className="p-4 text-center" colSpan={2}>
                          <span className="text-primary font-semibold">$100/hour</span>
                          <span className="text-muted-foreground text-sm ml-2">(3-hour minimum)</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="p-4 bg-muted/20 text-center text-sm text-muted-foreground">
                  All prices include 20% gratuity • Late-Night/Early Morning Surcharge ($35) applies 9 PM - 5 AM
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Trusted by Military Families</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Hear from families who've experienced our premium military transportation service
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="border-primary/10">
                  <CardContent className="p-6">
                    <Quote className="h-8 w-8 text-primary/30 mb-4" />
                    <p className="text-muted-foreground italic mb-4">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div>
                        <p className="font-semibold">{testimonial.name}</p>
                        <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                      </div>
                      <div className="flex gap-0.5">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Simple, seamless transportation for your military family needs
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    1
                  </div>
                  <h3 className="font-semibold mb-2">Book Online or Call</h3>
                  <p className="text-sm text-muted-foreground">
                    Schedule your ride with pickup location, destination, and passenger info
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    2
                  </div>
                  <h3 className="font-semibold mb-2">We Handle Access</h3>
                  <p className="text-sm text-muted-foreground">
                    Our DBIDS credentials ensure smooth base entry—no sponsor needed
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    3
                  </div>
                  <h3 className="font-semibold mb-2">Luxury Pickup</h3>
                  <p className="text-sm text-muted-foreground">
                    Your chauffeur arrives in a pristine Audi with complimentary water
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    4
                  </div>
                  <h3 className="font-semibold mb-2">Direct to Destination</h3>
                  <p className="text-sm text-muted-foreground">
                    We take you right where you need to go—no parking, no hassle
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Resource Section */}
        <section className="py-12 bg-card/50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-xl font-semibold mb-3">Learn More About JBSA Transportation</h3>
              <p className="text-muted-foreground mb-4">
                Read our comprehensive guide on JBSA military base access transportation, including credentials, requirements, and booking tips.
              </p>
              <Button asChild variant="outline">
                <Link to="/blog/jbsa-military-base-access-transportation-guide" className="inline-flex items-center gap-2">
                  Read the Complete JBSA Guide
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-primary/10 to-background">
          <div className="container mx-auto px-4 text-center">
            <Award className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Experience the Difference?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto text-lg">
              Join hundreds of military families who trust Rick's Black Car Service for their transportation needs at Joint Base San Antonio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <Link to="/request-quote">Book Your Ride</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>

      <Footer />
    </>
  );
};

export default MilitaryFamilies;
